import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { isPassPlanTrialAtMintEnabled } from '@/config/featureFlags';
import { wasMintedFromPassPlan } from '@/utils/passPlanAuth';
import { parseDiagnosticPrefill } from '@/data/diagnosticBank';
import { trackGtagEvent } from '@/utils/Analytics';
import { AppHandoffCard, examDayLabel } from '@/shared/components/AppHandoffCard';
import { isInternalTraffic } from '@/utils/internalTraffic';
import {
  detectSignupEntry,
  rememberSignupEntry,
  trackEndViewed,
  trackSignupStep,
  trackTurnCompleted,
  type SignupEntry,
} from '@/utils/signupRoad';
import { usePublicFunnelGate } from '@/hooks/usePublicFunnelGate';
import { trackMetaEvent } from '@/utils/metaPixel';
import { trackRedditCustomEvent, trackRedditEvent } from '@/utils/redditPixel';
import { SixTurnIntro } from './SixTurnIntro';
import {
  attachHandoffOnSignup,
  buildHandoffPayload,
  claimStartTracking,
  commitTurn,
  creditPrefill,
  ensureSession,
  getPrefetchedTurn,
  prefetchTurn,
  resetPublicDiagnosticSession,
  startOrResumeSession,
  usePublicDiagnosticSession,
} from './session';
import { PublicDiagnosticEndScreen } from './PublicDiagnosticEndScreen';
import { useKeepItSignIn } from './KeepIt';
import { TURN_KIND_BY_ID } from './turnMap';
import { TurnSlot } from './TurnSlot';
import type { TurnId, TurnResult } from './types';

/**
 * Same event and pixel mirrors as the classic /try start, so both variants share one funnel denominator.
 * A Start over run counts in GA4 but not as a second ad conversion for the same person.
 */
function trackStarted(prefill: ReturnType<typeof parseDiagnosticPrefill>, entry: SignupEntry, signedIn: boolean) {
  const { restarted } = ensureSession();
  // A Start over keeps the door the first run came in by.
  if (!restarted) rememberSignupEntry(entry);
  trackSignupStep('diagnostic_started', 'start', signedIn, {
    examType: 'unknown',
    diagnostic_variant: 'six_turn',
    ...(prefill ? { prefilled: true, prefillQuestionId: prefill.questionId } : {}),
  });
  if (restarted) return;
  trackMetaEvent('diagnostic_started', { examType: 'unknown' });
  trackRedditEvent('ViewContent');
  trackRedditCustomEvent('DiagnosticStart', { examType: 'unknown' });
}

export const TRY_TITLE = 'Free ASWB diagnostic: six turns, no account';
export const TRY_DESCRIPTION =
  'Try the free ASWB diagnostic: adaptive questions, a simulation beat, a flashcard, and Ask Pam. No account required to start.';

export interface PublicDiagnosticShellProps {
  /** Landing on `/` only: drop funnel events for a visitor who arrived signed in (see usePublicFunnelGate). */
  gateSignedInArrivals?: boolean;
  canonicalPath?: string;
  introPageTitle?: string;
  metaDescription?: string;
  /** Optional framing above the turn card (homepage hero). */
  heroSection?: React.ReactNode;
  /** Rendered under the turn card until the run is done (homepage value sections). */
  belowSection?: React.ReactNode;
}

/**
 * Six-turn orchestrator. Advances 1→6, loads only on prefetch miss,
 * hosts a slot per turn kind (B/C/D fill bodies).
 */
export const PublicDiagnosticShell: React.FC<PublicDiagnosticShellProps> = ({
  canonicalPath = '/try',
  gateSignedInArrivals = false,
  introPageTitle = TRY_TITLE,
  metaDescription = TRY_DESCRIPTION,
  heroSection,
  belowSection,
}) => {
  const state = usePublicDiagnosticSession();
  const [bootstrapped, setBootstrapped] = useState(false);
  const { pathname, search } = useLocation();
  const [prefillRaw] = useState(() => new URLSearchParams(search).get('prefill'));
  const [entry] = useState(() => detectSignupEntry(pathname, search));
  const navigate = useNavigate();
  const { currentUser, isStudentViewActive, isAuthLoading, userData } = useEnhancedAuth();
  const uid = isStudentViewActive ? undefined : currentUser?.uid;
  const signedInRef = useRef(false);
  signedInRef.current = Boolean(uid);
  const gate = usePublicFunnelGate(gateSignedInArrivals, Boolean(currentUser), Boolean(isAuthLoading));
  // Re-subscribe tick when prefetch cache flips without state change.
  const [, setPrefetchTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void startOrResumeSession()
      .then(() => creditPrefill(prefillRaw))
      .then((credited) => {
        if (cancelled) return;
        if (credited && claimStartTracking()) gate(() => trackStarted(parseDiagnosticPrefill(prefillRaw), entry, signedInRef.current));
        if (credited) gate(() => trackTurnCompleted(1, signedInRef.current, { prefilled: true }));
        setBootstrapped(true);
      });
    return () => {
      cancelled = true;
    };
  }, [prefillRaw, entry, gate]);

  const turnId = state.currentTurn as TurnId;
  const kind = TURN_KIND_BY_ID[turnId];
  const payload = getPrefetchedTurn(turnId);
  const showLoading = bootstrapped && payload == null;

  useEffect(() => {
    if (!bootstrapped) return;
    if (payload != null) return;
    void prefetchTurn(turnId).then(() => setPrefetchTick((n) => n + 1));
  }, [bootstrapped, turnId, payload]);

  const onComplete = (result: TurnResult) => {
    if (result.turnId === 6) gate(() => trackSignupStep('diagnostic_completed', 'complete', signedInRef.current, { diagnostic_variant: 'six_turn' }));
    // commitTurn persists + advances immediately; prefetch of next is non-blocking.
    void commitTurn(result).then(() => {
      if (claimStartTracking()) gate(() => trackStarted(null, entry, signedInRef.current));
      gate(() => trackTurnCompleted(result.turnId, signedInRef.current));
      setPrefetchTick((n) => n + 1);
    });
  };

  const done = state.results.length >= 6;
  // Derived, not a flag from creditPrefill: under StrictMode the second bootstrap sees turn 1 already credited.
  const prefill = parseDiagnosticPrefill(prefillRaw);
  const credited = state.results.some(
    (r) => r.turnId === 1 && 'questionId' in r && prefill != null && r.questionId === String(prefill.questionId) && r.choiceId === prefill.letter
  );
  const keepIt = useKeepItSignIn();
  // Auth lands before the trial and the claim do; hold the loader so the signed-in end screen never flashes.
  const saving = keepIt.pending != null && uid != null;

  // Hydrating auth reads as signed out; hold the end screen and its tracking until it settles.
  const endShown = bootstrapped && !showLoading && done && !isAuthLoading;
  const endTrackedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!endShown || endTrackedFor.current === state.sessionId) return;
    endTrackedFor.current = state.sessionId;
    gate(() => trackEndViewed(signedInRef.current));
  }, [endShown, state.sessionId, gate]);

  const onContinue = async () => {
    if (!uid) {
      navigate('/onboarding?src=public_diagnostic');
      return;
    }
    // Onboarding redirects a signed-in visitor (dashboard, or account setup when unprovisioned) before any claim, so claim here first.
    // Unbootstrapping shows the loader during the claim (no second tap) and stops a retired session prefetching a fresh turn 1.
    setBootstrapped(false);
    await attachHandoffOnSignup(uid, buildHandoffPayload());
    navigate('/onboarding?src=public_diagnostic');
  };

  const onStartOver = () => {
    gate(() => {
      if (!isInternalTraffic()) trackGtagEvent('diagnostic_restarted', { turnsCompleted: state.results.length });
    });
    // Hold the prefetch effect off so turn 1 is seeded by the start callable, not a bare next.
    setBootstrapped(false);
    resetPublicDiagnosticSession({ restarted: true });
    void startOrResumeSession().then(() => setBootstrapped(true));
  };

  // Each new turn (and the end screen) starts at its top, not wherever the last one was scrolled.
  const turnTopRef = useRef<HTMLDivElement>(null);
  const shownStep = useRef(`${turnId}:${done}`);
  useEffect(() => {
    const step = `${turnId}:${done}`;
    if (shownStep.current === step) return;
    shownStep.current = step;
    // Instant: html is scroll-smooth, and turn 4's clock starts at mount, not when a glide lands.
    turnTopRef.current?.scrollIntoView?.({ block: 'start', behavior: 'instant' });
  }, [turnId, done]);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="six-turn-shell">
      <Helmet>
        <title>{introPageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://www.socialworktestprepacademy.com${canonicalPath}`} />
      </Helmet>

      <div className="mx-auto max-w-2xl px-4 py-4 sm:py-12">
        {heroSection === undefined ? <SixTurnIntro /> : !done && heroSection}

        <div ref={turnTopRef} className="scroll-mt-20" />
        {!done && (
          <div className="mb-3 flex items-center justify-between text-sm text-neutral-600">
            <span>Turn {turnId} of 6</span>
            <span className="font-medium text-neutral-800">{state.results.length} complete</span>
          </div>
        )}

        {credited && !done && (
          <p
            data-testid="diagnostic-prefill-credit"
            className="mb-3 rounded-xl border-2 border-primary-100 bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-800"
          >
            You&apos;ve answered 1 of the 6 already. Finish the other five.
          </p>
        )}

        {saving ? (
          <div className="card" role="status" aria-live="polite" data-testid="keep-it-saving">
            <p className="text-sm text-neutral-600">Saving your six turns…</p>
          </div>
        ) : !bootstrapped || showLoading || (done && isAuthLoading) ? (
          <div
            className="rounded-2xl border-2 border-neutral-200 bg-white p-8 shadow-ledge"
            role="status"
            aria-live="polite"
            data-testid="public-diagnostic-loading"
          >
            <p className="text-sm text-neutral-600">
              Loading next turn…
            </p>
          </div>
        ) : done ? (
          <div data-testid="public-diagnostic-complete">
            <PublicDiagnosticEndScreen
              state={state}
              signedIn={Boolean(uid)}
              onContinue={() => void onContinue()}
              onStartOver={onStartOver}
              keepIt={keepIt}
              trialStarted={isPassPlanTrialAtMintEnabled() && userData?.isTrialActive === true && wasMintedFromPassPlan()}
            />
            {uid && (
              <div className="mx-auto mt-5 max-w-xl">
                <AppHandoffCard variant="result" examDay={examDayLabel(userData?.profile)} email={currentUser?.email} />
              </div>
            )}
          </div>
        ) : (
          <TurnSlot key={turnId} kind={kind} turnId={turnId} payload={payload} onComplete={onComplete} />
        )}
        {!done && belowSection}
      </div>
    </div>
  );
};

export default PublicDiagnosticShell;
